import { useCallback } from 'react';
import { useNotify } from 'react-admin';

import { PostPresignedUrlResponse } from '@Common/types/bo/presigned_url';

import { useCanAccess, useCreatePromise } from '@sh4res/bo-common/hooks';
import { getErrorMessage } from '@sh4res/bo-common/utils';
import { isLocalEnv } from '@sh4res/bo-common/utils/env';

export type UploadPresignedUrl = (content: File) => Promise<
  | {
      id: number;
      path: string;
      url: string;
    }
  | undefined
>;
export function useUploadPresignedUrl(): { upload: UploadPresignedUrl } {
  const [create] = useCreatePromise<object, PostPresignedUrlResponse>();
  const notify = useNotify();

  const canAccess = useCanAccess({
    resource: 'presigned_url',
    action: 'create',
  });

  const upload = useCallback(
    async (content: File) => {
      try {
        if (!canAccess) {
          return;
        }

        const result = await create('presigned_url', { data: {} });

        if (!isLocalEnv()) {
          await fetch(result.url, {
            method: 'PUT',
            body: content,
            headers: {
              'Content-Type': 'application/pdf',
            },
          });
        }

        return result;
      } catch (error: unknown) {
        notify('resources.presigned_url.upload.failure', {
          type: 'warning',
          messageArgs: { error: getErrorMessage(error) },
          multiLine: true,
        });
      }
    },
    [create, notify, canAccess],
  );

  return { upload };
}
